import Day from '@awesomecomponents/day-js';
import {
    BREAKPOINTS,
    Button,
    ButtonSelector,
    Checkbox,
    CheckboxGroup,
    DateOfBirthExperimental,
    Dropdown,
    ExpandablePanel,
    H2,
    MessageCard,
    Modal,
    TextInput,
    Tooltip,
    useMedia,
} from '@manulife/mux';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import { GetFamily } from '../App/ReducerActions/GetFamily';
import EditFamilyModalLoadingIndicator from '../LoadingIndicator/EditFamilyModalLoadingIndicator';
import {
    ButtonWrapper,
    ErrorMsg,
    FamilyCheckboxWrapper,
    InfoMessageWrapper,
    TextInputWrapper,
    ValidationAlertWrapper,
} from '../Shared/SharedStyles';
import { UpdateFamilyMember } from './ReducerActions/UpdateFamilyMember';
import { getEditFamilyValidationSchema } from './Validations';
import benefitCardEN from './assets/benefitCardEN.svg';
import benefitCardFR from './assets/benefitCardFR.svg';
import { cobBenefitsToDisplay } from './utils';
import { ADOBE } from '../../utils/AppConstants';

const DEFAULT_FAMILY_MEMBER = {
    hasOtherCoverage: false,
    gender: '',
    cobProvider: '',
    // Mux component CheckboxGroup expects object as a default value.
    kindOfCoverageSelected: {},
    firstName: '',
    lastName: '',
    // Mux component Date of birth expects object as a default value.
    dateOfBirth: {},
    cobCertificateNumber: '',
    cobContractNumber: '',
    familyMemberType: '',
    familyMemberAccuracyAgreement: false,
};

const EditFamilyModal = () => {
    const {
        isLoadingMemberData,
        isLoadingFamilyData,
        isLoadingMemberBenefitData,
        language,
        allowedDependentActions,
        memberBenefits,
        isEditFamilyDataSaving,
        currentFormDependent,
        errorUpdate,
        isCsr,
    } = useShallowEqualSelector((state) => ({
        isLoadingMemberData: state.MemberData.isLoadingMemberData,
        isLoadingFamilyData: state.MemberData.isLoadingFamilyData,
        isLoadingMemberBenefitData: state.MemberData.isLoadingMemberBenefitData,
        language: state.Authentication.language,
        allowedDependentActions: state.MemberData.allowedDependentActions,
        memberBenefits: state.MemberData.memberBenefits,
        isEditFamilyDataSaving: state.MemberData.isEditFamilyDataSaving,
        currentFormDependent: state.MemberData.currentFormDependent,
        errorUpdate: state.Errors.familyMemberUpdate,
        isCsr: state.Authentication.isCsr,
    }));

    const sendAdobeAnalyticsTransaction = (transactionName) => {
        window.adobeDataLayer?.push({
            event: ADOBE.TRANSACTION_COMPLETE,
            transaction: {
                transactionName,
                transactionCategory: 'Member Profile',
            },
        });
    };
    const history = useHistory();
    const dispatch = useDispatch();
    const errorRef = useRef();

    const benefitCardImage = language === 'fr-CA' ? benefitCardFR : benefitCardEN;
    const mobileView = useMedia([`(max-width: ${BREAKPOINTS.TABLET_PORTRAIT}px)`], [true], false);
    const customFieldStyle = mobileView ? { marginTop: '20px', width: '100%' } : { marginTop: '20px' };

    const cancelButton = useAEMContent(FRAGMENT_MP.common, 'cancelButton');
    const saveButton = useAEMContent(FRAGMENT_MP.common, 'saveButton');
    const toolTipButton = useAEMContent(FRAGMENT_MP.common, 'ariaTooltip');

    const editFamilyMemberHeader = useAEMContent(FRAGMENT_MP.familyMember, 'editFamilyMemberHeader');
    const addFamilyMemberMessage = useAEMContent(FRAGMENT_MP.familyMember, 'addFamilyMemberMessage', {
        parseHTML: true,
    });
    const firstNameLabel = useAEMContent(FRAGMENT_MP.familyMember, 'firstNameLabel');
    const lastNameLabel = useAEMContent(FRAGMENT_MP.familyMember, 'lastNameLabel');
    const dropDownPlaceholder = useAEMContent(FRAGMENT_MP.familyMember, 'dropDownPlaceholder');
    const relationshipLabel = useAEMContent(FRAGMENT_MP.familyMember, 'relationshipLabel');
    const childLabel = useAEMContent(FRAGMENT_MP.familyMember, 'childLabel');
    const spouseLabel = useAEMContent(FRAGMENT_MP.familyMember, 'spouseLabel');
    const cobLabel = useAEMContent(FRAGMENT_MP.familyMember, 'cobLabel');
    const cobToolTip = useAEMContent(FRAGMENT_MP.familyMember, 'cobToolTip', { parseHTML: true });
    const declarationMessage = useAEMContent(FRAGMENT_MP.familyMember, 'declarationMessage', { parseHTML: true });
    const cobYes = useAEMContent(FRAGMENT_MP.familyMember, 'cobYes');
    const cobNo = useAEMContent(FRAGMENT_MP.familyMember, 'cobNo');
    const cobType = useAEMContent(FRAGMENT_MP.familyMember, 'cobType');
    const providerType = useAEMContent(FRAGMENT_MP.familyMember, 'providerType');
    const benefitCardHeaderLabel = useAEMContent(FRAGMENT_MP.familyMember, 'benefitCardHeader');
    const benefitCardInstruction = useAEMContent(FRAGMENT_MP.familyMember, 'benefitCardInstructions');
    const providerManulife = useAEMContent(FRAGMENT_MP.familyMember, 'ProviderManulife');
    const providerOther = useAEMContent(FRAGMENT_MP.familyMember, 'ProviderOther');
    const contractNumber = useAEMContent(FRAGMENT_MP.familyMember, 'contractNumber');
    const certificateNumber = useAEMContent(FRAGMENT_MP.familyMember, 'certificateNumber');

    const validationMessages = {
        errorSummaryTitle: useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryTitle'),
        errorSummaryMessage: useAEMContent(FRAGMENT_MP.validationMessages, 'errorSummaryMessage'),
        cobProviderBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobProviderBlank'),
        cobPolicyBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobPolicyBlank'),
        cobCertBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobCertBlank'),
        cobBenefitBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobBenefitBlank'),
        cobCoverageSelectBlank: useAEMContent(FRAGMENT_MP.validationMessages, 'cobCoverageSelectBlank'),
        certifyCheckBox: useAEMContent(FRAGMENT_MP.validationMessages, 'certifyCheckBox'),
    };

    const isDataLoading = isLoadingMemberData || isLoadingFamilyData || isLoadingMemberBenefitData;

    const {
        handleSubmit,
        values,
        touched,
        errors,
        isValid,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        submitCount,
    } = useFormik({
        initialValues: {
            ...DEFAULT_FAMILY_MEMBER,
        },
        onSubmit: async () => {
            const response = await dispatch(UpdateFamilyMember(values, pendingCOBRecords));
            if (response.success) {
                dispatch(GetFamily());
                history.push('/gb/member-profile-2/family');
            }
        },
        validationSchema: Yup.object().shape(getEditFamilyValidationSchema(validationMessages)),
    });

    /**
     * Build an family member object to prepopulate the edit form and clear any errors from previous edit
     */
    useEffect(() => {
        if (currentFormDependent === undefined) {
            history.push('/gb/member-profile-2/family');
        } else {
            const familyMemberData = currentFormDependent;
            const { personId, firstName, lastName, birthDate, gender, familyMemberType, benefits } = familyMemberData;

            // Get the first COB record that has another insurer so the UI can pre-populate various common COB related fields
            const cobData = benefits.find((benefit) => benefit.cob?.hasOtherInsurer)?.cob || {};

            const {
                cobPolicyNumber = '',
                cobClaimCertNumber = '',
                otherInsurerName = '',
                hasOtherInsurer = false,
            } = cobData;

            // Find the benefits that are covered to prepolulate the benefit checkboxes
            const coveredBenefits = benefits
                .filter((benefit) => benefit.cob)
                .reduce((currentCoveredBenefits, benefit) => {
                    if (benefit.cob.hasOtherInsurer) {
                        currentCoveredBenefits[benefit.code] = true;
                    }
                    return currentCoveredBenefits;
                }, {});

            // The birthdate comes from redux as a string so we need to transform it to an object for the DateOfBirthExperimental component
            const [birthDay, birthMonth, birthYear] = new Day(birthDate)
                .format('en', 'DD MMM YYYY') // This doesn't need to be localized since the component always uses English values
                .toLowerCase()
                .split(' ');

            // We want to keep the button selector for other coverage unselected if they have any null cob record AND if they have an invalid cob
            const hasOtherCoverage = benefits.some((benefit) => benefit.cobRequired && !benefit.cob)
                ? ''
                : hasOtherInsurer;

            // If they have a MANULIFE cob record then it means they selected MANULIFE from the dropdown and we should preselect that value
            const preselectedInsurerName = benefits.some((benefit) => benefit.cob?.otherInsurerName === 'MANULIFE')
                ? 'MANULIFE'
                : otherInsurerName;

            setFieldValue('personId', personId, false);
            setFieldValue('firstName', firstName, false);
            setFieldValue('lastName', lastName, false);
            setFieldValue('gender', gender, false);
            setFieldValue('familyMemberType', familyMemberType, false);
            setFieldValue('dateOfBirth', {
                day: birthDay,
                month: birthMonth,
                year: birthYear,
                date: birthDate,
            });
            setFieldValue('cobContractNumber', cobPolicyNumber, false);
            setFieldValue('cobCertificateNumber', cobClaimCertNumber, false);
            setFieldValue('cobProvider', preselectedInsurerName, false);
            setFieldValue('hasOtherCoverage', hasOtherCoverage, false);
            setFieldValue('kindOfCoverageSelected', coveredBenefits);
            setFieldValue('familyMemberAccuracyAgreement', false, false);
            setFieldValue('benefits', benefits, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFormDependent, setFieldValue]);
    // TODO: Upgrade to useNavigate or redirect from useHistory which is going to be deprecated.
    // See: https://jira.manulife.ca/browse/GBSLTS-104, https://reactrouter.com/en/main/hooks/use-navigate, https://reactrouter.com/en/main/fetch/redirect

    const displayedCobBenefits = useMemo(
        () => !isDataLoading && cobBenefitsToDisplay(memberBenefits),
        [isDataLoading, memberBenefits]
    );

    /**
     * Build a list of COBs that have been selected on the UI so we can identify which COBs in MC need to be marked as 'hasOtherInsurer'
     */
    const pendingCOBRecords = useMemo(
        () =>
            !isDataLoading &&
            memberBenefits
                // It returns an array of benefits which are currently selected by the user while selecting the cobs.
                .filter((memberBenefit) => values.kindOfCoverageSelected[memberBenefit.code] && values.hasOtherCoverage)
                // Maps cobs field to build an array of cobs
                .map((coordinationOfBenefit) => ({
                    benefitCode: coordinationOfBenefit.code,
                    memberBenefitId: coordinationOfBenefit.memberBenefitId,
                    cobPolicyNumber: values.cobContractNumber,
                    cobClaimCertNumber: values.cobCertificateNumber,
                    otherInsurerName: values.cobProvider,
                    hasOtherInsurer: values.hasOtherCoverage,
                })),
        [isDataLoading, values, memberBenefits]
    );

    // Gets error messages to display in message card alert.
    const errorMessage = useMemo(
        () => validationMessages.errorSummaryMessage,
        [validationMessages.errorSummaryMessage]
    );

    // Scrolls to error messages at the top of the modal on submit.
    useEffect(() => {
        if (!isValid && isSubmitting) {
            errorRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [isValid, isSubmitting]);

    return (
        <Modal
            id="editFamilyMemberModal"
            aria-label="Edit a family member"
            isOpen={true}
            onClose={() => history.push('/gb/member-profile-2/family')}
        >
            {isDataLoading && <EditFamilyModalLoadingIndicator data-testid="editFamilyModalLoadingIndicator" />}
            {!isDataLoading && (
                <form onSubmit={handleSubmit}>
                    <H2>{editFamilyMemberHeader}</H2>
                    <InfoMessageWrapper>
                        <MessageCard variant={MessageCard.VARIANT.SECONDARY_INFORMATION}>
                            {addFamilyMemberMessage}
                        </MessageCard>
                    </InfoMessageWrapper>

                    <ValidationAlertWrapper
                        id="addFamilyMemberErrors"
                        ref={errorRef}
                        aria-hidden={isValid}
                        aria-live="polite"
                        aria-atomic="true"
                        role="alert"
                    >
                        {errorUpdate?.hasError && (
                            <MessageCard
                                variant={MessageCard.VARIANT.PRIMARY_ALERT}
                                titleText={validationMessages.errorSummaryTitle}
                            >
                                Problem: {errorUpdate.message} Code: {errorUpdate.code}
                            </MessageCard>
                        )}

                        {!isValid && submitCount > 0 && (
                            <MessageCard
                                variant={MessageCard.VARIANT.PRIMARY_ALERT}
                                titleText={validationMessages.errorSummaryTitle}
                            >
                                {errorMessage}
                            </MessageCard>
                        )}
                    </ValidationAlertWrapper>
                    <TextInputWrapper>
                        <TextInput
                            id="firstName"
                            label={firstNameLabel}
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.firstName}
                            disabled={true}
                            maxLength={30}
                        />
                        <TextInput
                            id="lastName"
                            label={lastNameLabel}
                            customStyle={{
                                rootStyle: {
                                    width: '100%',
                                },
                            }}
                            value={values.lastName}
                            disabled={true}
                            maxLength={30}
                        />
                    </TextInputWrapper>
                    <DateOfBirthExperimental
                        id="dateOfBirth"
                        value={values.dateOfBirth}
                        customStyle={{ fieldSetStyle: customFieldStyle }}
                        disabledDays={{
                            after: new Date(Day.TODAY.toDate()),
                            before: new Date(new Day([1900, 0, 1]).toDate()),
                        }}
                        disabled={true}
                    />
                    <Dropdown
                        id="familyMemberType"
                        label={relationshipLabel}
                        dropdownItems={[
                            allowedDependentActions.updateChild && {
                                label: childLabel,
                                value: 'CHILD',
                            },
                            allowedDependentActions.updateSpouse && { label: spouseLabel, value: 'SPOUSE' },
                        ].filter((item) => item)}
                        customStyle={{ rootStyle: customFieldStyle }}
                        value={values.familyMemberType}
                        placeholder={dropDownPlaceholder}
                        disabled={true}
                        data-testid="edit_fam_familyMemberType"
                    />
                    <ButtonSelector
                        id="hasOtherCoverage"
                        label={
                            <>
                                {cobLabel}
                                <Tooltip
                                    id="hasOtherCoverageTooltip"
                                    ariaLabel={`${cobLabel} ${toolTipButton}`}
                                    customStyle={{
                                        rootStyle: { marginLeft: '10px' },
                                    }}
                                    content={cobToolTip}
                                    bubbleWidth={200}
                                    variant={Tooltip.VARIANT.SECONDARY}
                                    side={Tooltip.SIDES.TOP}
                                ></Tooltip>
                            </>
                        }
                        values={[
                            {
                                label: cobYes,
                                value: 'true',
                            },
                            {
                                label: cobNo,
                                value: 'false',
                            },
                        ]}
                        customStyle={{ rootStyle: { marginTop: '20px' } }}
                        value={values.hasOtherCoverage.toString()}
                        onChange={(newValue) => {
                            setFieldValue('hasOtherCoverage', newValue === 'true');
                        }}
                        errors={
                            touched.hasOtherCoverage && [
                                {
                                    id: 'hasOtherCoverage',
                                    msg: errors.hasOtherCoverage,
                                    isError: Boolean(errors.hasOtherCoverage),
                                },
                            ]
                        }
                        verticalStackingBreakPoint={0}
                        disabled={isEditFamilyDataSaving}
                    ></ButtonSelector>
                    <div>
                        {values.hasOtherCoverage && (
                            <>
                                <CheckboxGroup
                                    direction="column"
                                    id="kindOfCoverageSelected"
                                    legend={cobType}
                                    values={displayedCobBenefits}
                                    value={values.kindOfCoverageSelected}
                                    customStyle={{
                                        legendStyle: {
                                            marginLeft: '-10px',
                                        },
                                        checkboxStyle: {
                                            marginLeft: '-8px',
                                        },
                                    }}
                                    onChange={(newValue) => {
                                        setFieldValue('kindOfCoverageSelected', newValue);
                                    }}
                                    selected={values.kindOfCoverageSelected}
                                    errors={
                                        touched.kindOfCoverageSelected && [
                                            {
                                                id: 'kindOfCoverageSelected',
                                                msg: errors.kindOfCoverageSelected,
                                                isError: Boolean(errors.kindOfCoverageSelected),
                                            },
                                        ]
                                    }
                                    disabled={isEditFamilyDataSaving}
                                    errorCallout
                                />
                                <Dropdown
                                    id="cobProvider"
                                    label={providerType}
                                    customStyle={{ rootStyle: customFieldStyle }}
                                    dropdownItems={[
                                        {
                                            label: providerManulife,
                                            value: 'MANULIFE',
                                        },
                                        {
                                            label: providerOther,
                                            value: 'OTHER',
                                        },
                                    ]}
                                    placeholder={dropDownPlaceholder}
                                    onChange={(newValue) => setFieldValue('cobProvider', newValue)}
                                    value={values.cobProvider}
                                    errors={
                                        touched.cobProvider && [
                                            {
                                                id: 'cobProvider',
                                                msg: errors.cobProvider,
                                                isError: Boolean(errors.cobProvider),
                                            },
                                        ]
                                    }
                                    disabled={isEditFamilyDataSaving}
                                    callouts
                                    ariaRequired
                                />
                            </>
                        )}

                        {values.hasOtherCoverage && values.cobProvider === 'MANULIFE' && (
                            <>
                                <ExpandablePanel
                                    id={'benefitsCardSummaryExpandablePanel'}
                                    title={benefitCardHeaderLabel}
                                    customStyle={{ expandablePanelStyle: { marginTop: '20px', marginBottom: '20px' } }}
                                >
                                    {benefitCardInstruction}
                                    <img
                                        src={benefitCardImage}
                                        alt="benefit card"
                                        style={{ maxWidth: '100%', display: 'block' }}
                                    />
                                </ExpandablePanel>
                                <TextInput
                                    id="cobContractNumber"
                                    label={contractNumber}
                                    customStyle={{ rootStyle: customFieldStyle }}
                                    value={values.cobContractNumber}
                                    onChange={(newValue) => setFieldValue('cobContractNumber', newValue)}
                                    onBlur={() => setFieldTouched('cobContractNumber', true)}
                                    errors={
                                        touched.cobContractNumber && [
                                            {
                                                id: 'cobContractNumber',
                                                msg: errors.cobContractNumber,
                                                isError: Boolean(errors.cobContractNumber),
                                            },
                                        ]
                                    }
                                    disabled={isEditFamilyDataSaving}
                                    maxLength={7}
                                    callouts
                                    ariaRequired
                                />
                                <TextInput
                                    id="cobCertificateNumber"
                                    label={certificateNumber}
                                    customStyle={{ rootStyle: customFieldStyle }}
                                    value={values.cobCertificateNumber}
                                    onChange={(newValue) => setFieldValue('cobCertificateNumber', newValue)}
                                    onBlur={() => setFieldTouched('cobCertificateNumber', true)}
                                    errors={
                                        touched.cobCertificateNumber && [
                                            {
                                                id: 'cobCertificateNumber',
                                                msg: errors.cobCertificateNumber,
                                                isError: Boolean(errors.cobCertificateNumber),
                                            },
                                        ]
                                    }
                                    disabled={isEditFamilyDataSaving}
                                    maxLength={9}
                                    forceUpper={true}
                                    callouts
                                    ariaRequired
                                />
                            </>
                        )}
                    </div>
                    <FamilyCheckboxWrapper>
                        <Checkbox
                            id="familyMemberAccuracyAgreement"
                            checked={values.familyMemberAccuracyAgreement}
                            onChange={(newValue) => setFieldValue('familyMemberAccuracyAgreement', newValue)}
                            error={
                                Boolean(errors.familyMemberAccuracyAgreement) && touched.familyMemberAccuracyAgreement
                            }
                            disabled={isEditFamilyDataSaving}
                            aria-required="true"
                        />
                        <label htmlFor="familyMemberAccuracyAgreement">
                            {declarationMessage}
                            {Boolean(errors.familyMemberAccuracyAgreement) && touched.familyMemberAccuracyAgreement && (
                                <ErrorMsg aria-atomic="true" aria-live="polite" role="alert">
                                    {errors.familyMemberAccuracyAgreement}
                                </ErrorMsg>
                            )}
                        </label>
                    </FamilyCheckboxWrapper>
                    <ButtonWrapper>
                        <Button
                            id="cancelEditFamilyButton"
                            variant={Button.VARIANT.SECONDARY}
                            onClick={() => {
                                sendAdobeAnalyticsTransaction('Edit My Family');
                                history.push('/gb/member-profile-2/family');
                            }}
                            disabled={isEditFamilyDataSaving}
                        >
                            {cancelButton}
                        </Button>
                        <Button
                            id="updateEditFamilyButton"
                            type="submit"
                            saving={isEditFamilyDataSaving}
                            disabled={isEditFamilyDataSaving || isCsr}
                            onClick={() => {
                                if (Object.keys(errors).length === 0) {
                                    sendAdobeAnalyticsTransaction('Edit My Family');
                                }
                            }}
                        >
                            {saveButton}
                        </Button>
                    </ButtonWrapper>
                </form>
            )}
        </Modal>
    );
};

export default EditFamilyModal;
